import React, { useState, useId } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { BiCalendar } from "react-icons/bi";
import { IoClose } from "react-icons/io5";

const inputTypes = {
  default:
    "pc-input border rounded w-full px-[7px] py-[3px] h-7 font-sans leading-tight focus:outline-none focus:ring-1 focus:ring-brandAccent",
  radio:
    "px-2 py-1 text-base border border-borderInput focus:outline-none focus:ring-1 focus:ring-brandAccent",
};

// ✅ Accessible custom checkbox
const CheckBox = ({ id, name, checked = false, onChange, disabled }) => (
  <input id={id} name={name} type="checkbox" checked={!!checked}
    onChange={onChange} disabled={disabled}
    className="h-5 w-5 cursor-pointer rounded-sm disabled:cursor-not-allowed" />
);

// Format helpers
const formatDateForDisplay = (dateStr) => {
  if (!dateStr || dateStr.trim() === "") return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}/${day}/${year}`;
};

const CustomInput = React.forwardRef(({
  label,
  inputType,
  required,
  type,
  extraClass,
  maxLength,
  disabled,
  value,
  onChange,
  disablePastDates = false,
  autoComplete,
  ...props
}, ref) => {
  const generatedId = useId();
  const [showPassword, setShowPassword] = useState(false);

  const handleInput = (event) => {
    if (type === "number" && maxLength) {
      event.target.value = event.target.value.slice(0, maxLength);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const displayValue = type === "date" ? formatDateForDisplay(value) : value;

  const handleDateChange = (selectedDates) => {
    if (onChange && selectedDates.length > 0) {
      const localDate = selectedDates[0];
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, "0");
      const day = String(localDate.getDate()).padStart(2, "0");

      const formattedValue = `${year}-${month}-${day}`;

      onChange({
        target: {
          value: formattedValue,
          name: props.name,
          type: "date",
        },
      });
    }
  };

  const handleKeyDown = (event) => {
    if (type === "number" && ["e", "E", "+", "-", "."].includes(event.key)) {
      event.preventDefault();
    }
  };

  const getAutoCompleteValue = () => {
    if (autoComplete !== undefined) return autoComplete;
    if (type === "password") return "new-password";
    if (type === "email") return "email";
    if (type === "tel") return "tel";
    if (type === "date") return "off";
    return "off";
  };

  const resolvedId = props.id || props.name || generatedId;
  const today = new Date();

  return (
    <div
      className={`flex ${
        ["radio", "checkbox"].includes(type)
          ? "flex flex-row-reverse w-fit items-center"
          : "flex-col w-full"
      }`}
    >
      {label && (
        <label
          htmlFor={resolvedId}
          className={`${required ? "required" : ""} ${
            ["checkbox"].includes(type)
              ? "ml-2 min-w-[63px] font-sans text-sm"
              : ["radio"].includes(type)
              ? "ml-2 mb-2 min-w-[63px] font-sans text-sm"
              : "font-sans mb-1 text-sm"
          }`}
        >
          {label}
        </label>
      )}

      {type === "checkbox" ? (
        <CheckBox
          id={resolvedId}
          name={props.name}
          checked={props.checked}
          onChange={onChange}
          disabled={disabled}
        />
      ) : type === "date" ? (
        <div className="relative w-full">
          <Flatpickr
            key={disablePastDates ? today.toISOString() : "flatpickr"}
            id={resolvedId}
            className={`${inputTypes[inputType] || inputTypes.default} ${
              extraClass || ""
            } disabled:cursor-not-allowed pl-8 pr-8`}
            placeholder="MM/DD/YYYY"
            options={{
              dateFormat: "m/d/Y",
              allowInput: true,
              wrap: false,
              minDate: disablePastDates ? today : undefined, // ✅ Date object instead of "today"
              now: today, // ✅ ensures Flatpickr highlights this same "today"
              defaultDate: displayValue || today, // highlight consistent
            }}
            value={displayValue}
            onChange={handleDateChange}
            disabled={disabled}
            {...props}
            autoComplete={getAutoCompleteValue()}
          />

          <BiCalendar className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />

          {value && !disabled && (
            <button
              type="button"
              aria-label={`Clear ${label || props["aria-label"] || "date"}`}
              onClick={() =>
                onChange?.({
                  target: { value: "", name: props.name, type: "date" },
                })
              }
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-600 focus:outline-none focus:ring-1 focus:ring-brandAccent rounded"
            >
              <IoClose />
            </button>
          )}
        </div>
      ) : (
        <div className="relative w-full">
          <input
            ref={ref}
            id={resolvedId}
            type={
              type === "password"
                ? showPassword
                  ? "text"
                  : "password"
                : type || "text"
            }
            placeholder={type === "date" ? "YYYY-MM-DD" : undefined}
            maxLength={type !== "number" ? maxLength : undefined}
            {...props}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            className={`${inputTypes[inputType] || inputTypes.default} ${
              extraClass || ""
            } disabled:cursor-not-allowed ${
              type === "password" ? "pr-8" : ""
            } [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            disabled={disabled}
            autoComplete={getAutoCompleteValue()}
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />

          {type === "password" && (
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-brandAccent rounded"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export default CustomInput;
